import { useState } from "react";
import { currency } from "@/lib/api";

const MONTHLY_R = 0.06 / 12;
const MAX_BANK = 30;
const RESALE_APPRECIATION = 30;

const annuity = (principal, rate, n) =>
  rate === 0 ? principal / n : (principal * rate) / (1 - Math.pow(1 + rate, -n));

const Slider = ({ label, value, display, min, max, step, onChange, testId }) => (
  <div className="mt-4">
    <div className="flex items-center justify-between">
      <p className="text-sm text-pine">{label}</p>
      <p className="font-mono text-xs text-terracotta" data-testid={`${testId}-value`}>{display}</p>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testId}
      aria-label={label}
      className="range-light mt-2"
      style={{ "--fill": `${((value - min) / (max - min)) * 100}%` }}
    />
  </div>
);

const Stat = ({ label, value, dark, testId }) => (
  <div className={`rounded-lg p-3 text-center ${dark ? "bg-pine" : "bg-sand/70"}`}>
    <p className={`font-mono text-[9px] uppercase tracking-[0.14em] ${dark ? "text-cream/60" : "text-sage"}`}>{label}</p>
    <p className={`mt-1 font-display text-sm md:text-base ${dark ? "text-gold" : ""}`} data-testid={testId}>{value}</p>
  </div>
);

function Instalments({ price, slug }) {
  const [months, setMonths] = useState(12);
  const monthly = months === 1 ? price : annuity(price, MONTHLY_R, months);
  const total = monthly * months;
  return (
    <div data-testid={`instalments-${slug}`}>
      <Slider
        label="Number of monthly payments"
        value={months}
        display={`${months} ${months === 1 ? "payment" : "payments"}`}
        min={1}
        max={12}
        step={1}
        onChange={setMonths}
        testId={`instalment-months-${slug}`}
      />
      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat label="Per month" value={currency(monthly)} testId={`instalment-monthly-${slug}`} />
        <Stat label="Duration" value={`${months} mo`} testId={`instalment-duration-${slug}`} />
        <Stat label="Total paid" value={currency(total)} dark testId={`instalment-total-${slug}`} />
      </div>
      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.14em] text-sage">
        Spread the purchase over up to 12 months — title transfers on the final payment
      </p>
    </div>
  );
}

function Financing({ price, incomeRate, incomeLabel, slug }) {
  const [bankPct, setBankPct] = useState(30);
  const [bankRate, setBankRate] = useState(9);
  const [years, setYears] = useState(10);
  const loan = (price * bankPct) / 100;
  const cash = price - loan;
  const bankYearly = loan > 0 ? annuity(loan, bankRate / 100, years) : 0;
  const gross = (price * incomeRate) / 100;
  const net = gross - bankYearly;
  const roiCash = cash > 0 ? (net / cash) * 100 : 0;
  const resale = price * (1 + RESALE_APPRECIATION / 100);
  const netTenYears = net * 10;
  const exitProfit = resale - cash + netTenYears;
  return (
    <div data-testid={`financing-${slug}`}>
      <Slider
        label="Bank-financed share"
        value={bankPct}
        display={`${bankPct}% · ${currency(loan)}`}
        min={0}
        max={MAX_BANK}
        step={5}
        onChange={setBankPct}
        testId={`finance-bank-pct-${slug}`}
      />
      <Slider
        label="Bank interest rate"
        value={bankRate}
        display={`${bankRate}% p.a.`}
        min={5}
        max={14}
        step={0.5}
        onChange={setBankRate}
        testId={`finance-bank-rate-${slug}`}
      />
      <Slider
        label="Loan term"
        value={years}
        display={`${years} years`}
        min={3}
        max={20}
        step={1}
        onChange={setYears}
        testId={`finance-years-${slug}`}
      />
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Stat label="Cash you pay" value={currency(cash)} testId={`finance-cash-${slug}`} />
        <Stat label="Bank pays" value={currency(loan)} testId={`finance-loan-${slug}`} />
        <Stat label={`Gross ${incomeLabel} / yr`} value={currency(gross)} testId={`finance-gross-${slug}`} />
        <Stat label="Bank repayment / yr" value={`− ${currency(bankYearly)}`} testId={`finance-bank-yearly-${slug}`} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <Stat label="Net return / yr" value={currency(net)} testId={`finance-net-${slug}`} />
        <Stat
          label="ROI on your cash"
          value={`${roiCash.toFixed(1)}% p.a.`}
          dark
          testId={`finance-roi-${slug}`}
        />
      </div>
      <div className="mt-5 rounded-lg border border-pine/10 bg-sand/40 p-3" data-testid={`finance-exit-${slug}`}>
        <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-moss">
          Future resale · average +{RESALE_APPRECIATION}% within 10 years
        </p>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <Stat label="Your cash in" value={currency(cash)} testId={`finance-exit-cash-${slug}`} />
          <Stat label="Resale price" value={currency(resale)} testId={`finance-resale-${slug}`} />
          <Stat label="Net rental · 10y" value={currency(netTenYears)} testId={`finance-exit-net10-${slug}`} />
          <Stat label="Final profit" value={currency(exitProfit)} dark testId={`finance-exit-profit-${slug}`} />
        </div>
        <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.14em] text-sage">
          Selling price − (purchase price − bank part) + 10 × (gross rental − bank repayment)
        </p>
      </div>
      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.14em] text-sage">
        All-cash ROI {incomeRate}% p.a. · part of the {incomeLabel} services the bank, the rest is yours — illustrative, not a guarantee
      </p>
    </div>
  );
}

export const PaymentOptions = ({ price, priceNote, incomeRate, incomeLabel, slug, financing = true }) => {
  const [tab, setTab] = useState("instalments");
  return (
    <div className="rounded-xl border border-pine/10 bg-white p-5" data-testid={`payment-options-${slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-moss">
          {financing ? "How to purchase" : "How to purchase · pay in instalments"}
        </p>
        {financing && (
        <div className="flex gap-1 rounded-full bg-sand/70 p-1">
          {[
            ["instalments", "Pay in instalments"],
            ["finance", "Cash + bank finance"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`payment-tab-${k === "finance" ? "finance" : "instalments"}-${slug}`}
              className={`rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-200 ${
                tab === k ? "bg-pine text-cream" : "text-moss hover:text-pine"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        )}
      </div>
      <p className="mt-3 font-display text-lg text-pine" data-testid={`payment-price-${slug}`}>
        {currency(price)}
        {priceNote && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.14em] text-moss">{priceNote}</span>}
      </p>
      {tab === "instalments" || !financing ? (
        <Instalments price={price} slug={slug} />
      ) : (
        <Financing price={price} incomeRate={incomeRate} incomeLabel={incomeLabel} slug={slug} />
      )}
    </div>
  );
};
